<template>
  <div>
    <v-card elevation="0">
      <v-card-title class="primary white--text">
        <v-spacer></v-spacer>
        <div>New Scheme Plan Property</div>
        <v-spacer></v-spacer>
        <v-btn icon @click="$emit('close')">
          <v-icon class="white--text">mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-layout column>
          <v-flex xs12>
            <v-form v-model="valid" ref="form" lazy-validation>
              <v-layout row wrap class="mt-2">
                <!-- Adjudication -->
                <v-flex xs12 md4>
                  <div class="pa-2">
                    <div>Adjudication<span class="error--text"> *</span></div>
                    <v-autocomplete class="text-field-background" outlined dense
                      v-model="adjudication_selected"
                      :items="adjudications"
                      :loading="loadingAdjudications"
                      item-text="name"
                      item-value="id"
                      return-object
                      :rules="[v => !!v || 'Adjudication is required']"
                      placeholder="Select adjudication"
                      :error-messages="serverErrors.lr_number"
                      @input="serverErrors.lr_number = null" />
                  </div>
                </v-flex>
                <!-- Parcel Number -->
                <v-flex xs12 md4>
                  <div class="pa-2">
                    <div>Parcel Number<span class="error--text"> *</span></div>
                    <v-text-field class="text-field-background" outlined dense
                      v-model="parcel_number"
                      placeholder="e.g. 45"
                      :rules="parcelRules" />
                  </div>
                </v-flex>
                <!-- Title Deed Property Number -->
                <v-flex xs12 md4>
                  <div class="pa-2">
                    <div>Property No. (Title Deed)<span class="error--text"> *</span></div>
                    <v-text-field class="text-field-background" outlined dense v-model="form.title_deed_pn"
                      placeholder="e.g. P.N. 456/890" :rules="inputRequiredRules"></v-text-field>
                  </div>
                </v-flex>
                <!-- Size -->
                <v-flex xs12 md4>
                  <div class="pa-2">
                    <div>
                      Size of Property (m²)<span class="error--text"> *</span>
                    </div>
                    <v-text-field class="text-field-background" outlined dense v-model.number="form.size_sqm"
                      type="number" min="500" placeholder="e.g. 500" suffix="m²" :rules="sizeRules"></v-text-field>
                  </div>
                </v-flex>
                <!-- Sub-County -->
                <v-flex xs12 md4>
                  <div class="pa-2">
                    <div>SubCounty<span class="error--text"> *</span></div>
                    <v-autocomplete :items="subCounties" v-model="form.sub_county_id" class="text-field-background"
                      item-value="id" item-text="sub_county_name" @input="onSubCountyChange" outlined dense
                      :rules="inputRules"></v-autocomplete>
                  </div>
                </v-flex>
                <!-- Ward -->
                <v-flex xs12 md4>
                  <div class="pa-2">
                    <div>Ward<span class="error--text"> *</span></div>
                    <v-autocomplete :items="filteredWards" v-model="form.ward_id" class="text-field-background"
                      item-value="id" item-text="ward_name" @input="onWardChange" outlined dense
                      :rules="inputRules"></v-autocomplete>
                  </div>
                </v-flex>
                <!-- Town -->
                <v-flex xs12 md4>
                  <div class="pa-2">
                    <div>Town<span class="error--text"> *</span></div>
                    <v-autocomplete :items="filteredTowns" v-model="form.town_id" class="text-field-background"
                      item-value="id" item-text="town_name" outlined dense :rules="inputRules"></v-autocomplete>
                  </div>
                </v-flex>
                <!-- Physical Location -->
                <v-flex xs12 md4>
                  <div class="pa-2">
                    <div>Physical Location</div>
                    <v-text-field class="text-field-background" outlined dense v-model="form.physical_location"
                      placeholder="e.g. Near market, plot 12"></v-text-field>
                  </div>
                </v-flex>
                <!-- Latitude -->
                <v-flex xs12 md4>
                  <div class="pa-2">
                    <div class="d-flex justify-space-between align-center">
                      <span>Latitude</span>
                      <div>
                        <v-btn small text color="primary" class="mr-2" @click="openMapDialog">
                          Drop pin on map
                        </v-btn>
                      </div>
                    </div>
                    <v-text-field class="text-field-background" outlined dense v-model.number="form.latitude"
                      type="number" placeholder="-0.123456"></v-text-field>
                  </div>
                </v-flex>
                <!-- Longitude -->
                <v-flex xs12 md4>
                  <div class="pa-2">
                    <div>Longitude</div>
                    <v-text-field class="text-field-background" outlined dense v-model.number="form.longitude"
                      type="number" placeholder="37.123456"></v-text-field>
                  </div>
                </v-flex>
                <!-- Title Deed Upload -->
                <v-flex xs12 md4>
                  <div class="pa-2">
                    <div>Title Deed Scan (PDF — max 10 MB)</div>
                    <v-file-input v-model="titleDeedFile" accept="application/pdf"
                      prepend-icon="mdi-file-upload" class="text-field-background mt-1" outlined dense show-size
                      :rules="fileRules" @change="onFileSelected"></v-file-input>
                  </div>
                </v-flex>
              </v-layout>

              <!-- GPS info display -->
              <v-layout row wrap v-if="form.latitude && form.longitude" class="mt-2">
                <v-flex xs12 md8>
                  <div class="pa-3">
                    <v-alert type="info" dense text>
                      <strong>Selected GPS:</strong> {{ form.latitude }},
                      {{ form.longitude }}
                    </v-alert>
                  </div>
                </v-flex>
              </v-layout>
            </v-form>
          </v-flex>
        </v-layout>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn depressed class="secondary text-none mt-3" @click="submit" :disabled="!valid" :loading="submitting">
          Create Property
          <v-icon right>mdi-content-save</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Map Dialog -->
    <v-dialog v-model="mapDialog" max-width="1000" width="1000" eager>
      <v-card>
        <v-card-title class="primary white--text">
          <span>Select Location on Map</span>
          <v-spacer></v-spacer>
          <v-btn small dark text class="mr-2" @click="toggleMapStyle">
            <v-icon left small>{{ mapSatellite ? 'mdi-map' : 'mdi-satellite-variant' }}</v-icon>
            {{ mapSatellite ? 'Street' : 'Satellite' }}
          </v-btn>
          <v-btn icon dark @click="mapDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <div style="position: relative;">
            <div ref="mapContainer" style="height: 500px; width: 100%;"></div>
            <!-- Place search overlay -->
            <div style="position: absolute; top: 10px; left: 10px; z-index: 10; width: 290px;">
              <v-text-field
                v-model="mapSearchQuery"
                placeholder="Search for a place..."
                outlined dense hide-details clearable
                :loading="mapSearchLoading"
                background-color="white"
                class="elevation-2"
                prepend-inner-icon="mdi-magnify"
                @input="onMapSearchInput"
                @click:clear="mapSearchResults = []"
              />
              <v-list v-if="mapSearchResults.length" dense class="elevation-4 mt-0" style="max-height: 220px; overflow-y: auto; background: white; border-radius: 0 0 4px 4px;">
                <v-list-item
                  v-for="(result, i) in mapSearchResults"
                  :key="i"
                  style="cursor: pointer;"
                  @click="selectMapSearchResult(result)"
                >
                  <v-list-item-content>
                    <v-list-item-title class="text-caption" style="white-space: normal;">{{ result.display_name }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="mapDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="applyMapSelection" :disabled="!mapMarker">
            Use Coordinates
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import apiCall from "@/utils/api";
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

export default {
  name: "CreateSchemePlanPropertyForm",

  components: {},

  data() {
    return {
      valid: true,
      submitting: false,

      // Form model
      form: {
        title_deed_pn: "",
        sub_county_id: null,
        ward_id: null,
        town_id: null,
        physical_location: "",
        latitude: null,
        longitude: null,
        size_sqm: null,
      },
      adjudication_selected: null,
      parcel_number: "",
      adjudications: [],
      loadingAdjudications: false,
      titleDeedFile: null,
      serverErrors: {},

      // Lookup data
      subCounties: [],

      // Map
      mapDialog: false,
      mapSatellite: false,
      mapMarker: null,  // [lat, lng]
      mapSearchQuery: '',
      mapSearchResults: [],
      mapSearchLoading: false,
      _mapSearchTimeout: null,

      // Validation rules
      inputRequiredRules: [(v) => !!v || "Input is required"],
      inputRules: [(v) => !!v || "Input is required"],
      sizeRules: [
        (v) => !!v || "Input is required",
        (v) => (v && v >= 500) || "Size must be at least 500 m²",
      ],
      fileRules: [
        (v) => !v || v.type === 'application/pdf' || 'Only PDF files are allowed',
        (v) =>
          !v || v.size <= 10 * 1024 * 1024 || "File must be less than 10 MB",
      ],
      parcelRules: [
        (v) => !!v || "Parcel number is required",
        (v) => /^\d+$/.test(v) || "Parcel number must be numeric",
        (v) => (v && v.length <= 8) || "Parcel number must be at most 8 digits",
      ],
    };
  },

  computed: {
    filteredWards() {
      if (!this.form.sub_county_id) return [];
      const sc = this.subCounties.find(
        (s) => s.id === this.form.sub_county_id
      );
      return sc?.wards || [];
    },

    filteredTowns() {
      if (!this.form.ward_id) return [];
      const ward = this.filteredWards.find((w) => w.id === this.form.ward_id);
      return ward?.towns || [];
    },
  },

  async created() {
    await this.loadFormData();
  },

  methods: {
    // ── Data loading ──────────────────────────────────────────────────────

    async loadFormData() {
      try {
        const data = await apiCall({
          url: "/api/client/scheme-plan-property/form-data",
          method: "GET",
        });
        this.subCounties = data.subCounties || [];
        this.adjudications = data.adjudications || [];
      } catch (e) {
        this.$emit("save-error", {
          response: { data: { message: "Failed to load form data" } },
        });
      }
    },

    // ── Location cascades ─────────────────────────────────────────────────

    onSubCountyChange() {
      this.form.ward_id = null;
      this.form.town_id = null;
    },

    onWardChange() {
      this.form.town_id = null;
    },

    // ── File handling ─────────────────────────────────────────────────────

    onFileSelected(file) {
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) {
        this.titleDeedFile = null;
      }
    },

    // ── Map ───────────────────────────────────────────────────────────────

    openMapDialog() {
      const hasCoords = !!(this.form.latitude && this.form.longitude);
      this.mapMarker = hasCoords
        ? [Number(this.form.latitude), Number(this.form.longitude)]
        : null;
      this.mapDialog = true;
      this.$nextTick(() => {
        const center = hasCoords
          ? [Number(this.form.longitude), Number(this.form.latitude)]
          : [37.45, -0.45];
        const zoom = hasCoords ? 15 : 10;
        if (this._mapInstance) { this._mapInstance.remove(); this._mapInstance = null; }
        this._mapInstance = new maplibregl.Map({
          container: this.$refs.mapContainer,
          style: {
            version: 8,
            sources: { osm: { type: 'raster', tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'], tileSize: 256, attribution: '\u00a9 OpenStreetMap contributors' } },
            layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
          },
          center,
          zoom,
        });
        this._mapInstance.addControl(new maplibregl.NavigationControl(), 'top-right');
        if (hasCoords) {
          this._markerInstance = new maplibregl.Marker()
            .setLngLat([Number(this.form.longitude), Number(this.form.latitude)])
            .addTo(this._mapInstance);
        }
        this._mapInstance.on('click', (e) => {
          const { lng, lat } = e.lngLat;
          this.mapMarker = [Number(lat.toFixed(6)), Number(lng.toFixed(6))];
          if (this._markerInstance) this._markerInstance.remove();
          this._markerInstance = new maplibregl.Marker()
            .setLngLat([lng, lat])
            .addTo(this._mapInstance);
        });
      });
    },

    onMapSearchInput(val) {
      clearTimeout(this._mapSearchTimeout);
      this.mapSearchResults = [];
      if (!val || val.trim().length < 3) return;
      this._mapSearchTimeout = setTimeout(() => this.searchMapPlace(val), 400);
    },

    async searchMapPlace(query) {
      this.mapSearchLoading = true;
      try {
        const params = new URLSearchParams({ q: query, format: 'json', limit: 5, countrycodes: 'ke', addressdetails: 1 });
        const resp = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, { headers: { 'Accept-Language': 'en', 'User-Agent': 'KirinyagaRMS/1.0' } });
        this.mapSearchResults = await resp.json();
      } catch (e) {
        console.error('Place search failed', e);
      } finally {
        this.mapSearchLoading = false;
      }
    },

    selectMapSearchResult(result) {
      const lng = parseFloat(result.lon);
      const lat = parseFloat(result.lat);
      this.mapSearchResults = [];
      this.mapSearchQuery = '';
      if (!this._mapInstance) return;
      this._mapInstance.flyTo({ center: [lng, lat], zoom: 15 });
      this.mapMarker = [Number(lat.toFixed(6)), Number(lng.toFixed(6))];
      if (this._markerInstance) this._markerInstance.remove();
      this._markerInstance = new maplibregl.Marker()
        .setLngLat([lng, lat])
        .addTo(this._mapInstance);
    },

    toggleMapStyle() {
      this.mapSatellite = !this.mapSatellite;
      if (!this._mapInstance) return;
      const style = this.mapSatellite
        ? { version: 8, sources: { esri: { type: 'raster', tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'], tileSize: 256, attribution: 'Tiles \u00a9 Esri' } }, layers: [{ id: 'esri', type: 'raster', source: 'esri' }] }
        : { version: 8, sources: { osm: { type: 'raster', tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'], tileSize: 256, attribution: '\u00a9 OpenStreetMap contributors' } }, layers: [{ id: 'osm', type: 'raster', source: 'osm' }] };
      this._mapInstance.setStyle(style);
    },

    applyMapSelection() {
      if (!this.mapMarker) return;
      [this.form.latitude, this.form.longitude] = this.mapMarker;
      this.mapDialog = false;
    },

    // ── Submit ────────────────────────────────────────────────────────────

    async submit() {
      if (this.$refs.form && !this.$refs.form.validate()) return;

      this.submitting = true;
      try {
        const payload = new FormData();
        const lr_number = (this.adjudication_selected ? this.adjudication_selected.name : '') + '/' + this.parcel_number;
        payload.append("lr_number", lr_number.trim());
        payload.append("title_deed_pn", this.form.title_deed_pn);
        payload.append("sub_county_id", this.form.sub_county_id);
        payload.append("ward_id", this.form.ward_id);
        payload.append("town_id", this.form.town_id);
        if (this.form.physical_location)
          payload.append("physical_location", this.form.physical_location);
        if (this.form.latitude)
          payload.append("latitude", this.form.latitude);
        if (this.form.longitude)
          payload.append("longitude", this.form.longitude);
        payload.append("size_sqm", this.form.size_sqm);
        if (this.titleDeedFile)
          payload.append("title_deed_scan", this.titleDeedFile);

        const response = await apiCall({
          url: "/api/client/scheme-plan-property",
          method: "POST",
          data: payload,
        });

        // Download printout PDF
        try {
          const blob = await apiCall({
            url: `/api/client/scheme-plan-property/${response.id}/print`,
            method: "GET",
            responseType: "blob",
          });
          const blobUrl = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
          const link = document.createElement("a");
          link.href = blobUrl;
          link.setAttribute("download", `Scheme Plan Property ${response.pn}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
        } catch (e) {
          console.error("Printout download failed", e);
        }

        this.$emit("save-success", response);
        this.resetForm();
      } catch (error) {
        const errors = error?.response?.data?.errors;
        if (errors) {
          this.serverErrors = Object.fromEntries(
            Object.entries(errors).map(([k, v]) => [
              k,
              Array.isArray(v) ? v[0] : v,
            ])
          );
        }
        this.$emit("save-error", error);
      } finally {
        this.submitting = false;
      }
    },

    // ── Helpers ───────────────────────────────────────────────────────────

    resetForm() {
      this.titleDeedFile = null;
      this.serverErrors = {};
      this.adjudication_selected = null;
      this.parcel_number = "";
      this.form = {
        title_deed_pn: "",
        sub_county_id: null,
        ward_id: null,
        town_id: null,
        physical_location: "",
        latitude: null,
        longitude: null,
        size_sqm: null,
      };
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
  },
};
</script>
